/* eslint-disable jsx-a11y/anchor-is-valid */
import { readFirstFactor } from '../../../../lib/twoFactor/firstFactor'
import { useFirstFactorRouter } from '../../../../hooks/useFirstFactorRouter'
import { useTwoFactorChallengeStore } from '../../../../store/twoFactorChallengeStore'
import { useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { useFormik } from 'formik'
import { Dispatch } from 'react'
import useGetUserData from '../../../../Utils/useGetUserData'
import { useIntl } from 'react-intl'
import { Eye, EyeOff } from 'lucide-react'
import { useLogin } from '../../../../hooks/queries'
import { authApi } from '../../../../services/api'

const initialValues = {
  email: '',
  password: '',
}
interface fetchLoginValueProps {
  email: string
  password: string
}
/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
  const [loadingGoogle, setLoadingGoogle] = useState(false)
  const [pass, setPass] = useState(false)
  const getUserData = useGetUserData()
  const intl = useIntl()

  // Use React Query login mutation
  const loginMutation = useLogin()
  const routeFirstFactor = useFirstFactorRouter()
  const challengeNotice = useTwoFactorChallengeStore((state) => state.notice)
  const clearChallengeNotice = useTwoFactorChallengeStore((state) => state.clearNotice)

  /**
   * Defines the validation schema for the login form using Yup.
   * @constant
   */
  const loginSchema = Yup.object().shape({
    email: Yup.string()
      .email(intl.formatMessage({ id: 'Wrong email format' }))
      .min(3, intl.formatMessage({ id: 'Minimum 3 symbols' }))
      .max(50, intl.formatMessage({ id: 'Maximum 50 symbols' }))
      .required(intl.formatMessage({ id: 'Email is required' })),
    password: Yup.string()
      .min(3, intl.formatMessage({ id: 'Minimum 3 symbols' }))
      .required(intl.formatMessage({ id: 'Password is required' })),
  })

  /**
   * Handles login submission using React Query
   * @async
   * @param {fetchLoginValueProps} values - The login credentials.
   * @param {Dispatch<any>} setStatus - Function to set the status of the login attempt.
   * @param {Dispatch<any>} setSubmitting - Function to set the submitting state of the form.
   * @returns {Promise<void>}
   */
  const fetchLoginToken = async (
    values: fetchLoginValueProps,
    setStatus: Dispatch<any>,
    setSubmitting: Dispatch<any>
  ) => {
    clearChallengeNotice()
    try {
      const response = await loginMutation.mutateAsync({
        email: values.email,
        password: values.password,
      })

      // A 200 is not "signed in": a second-factor challenge carries no cookies,
      // so branch on the response instead of assuming a session.
      routeFirstFactor(readFirstFactor(response), () => getUserData())

    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || 'The login detail is incorrect'
      setStatus(errorMessage)
      setSubmitting(false)
    }
  }

  /**
   * Initializes and configures the Formik form for login.
   * @constant
   */
  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: (
      values: fetchLoginValueProps,
      { setStatus, setSubmitting }: { setStatus: Dispatch<any>; setSubmitting: Dispatch<any> }
    ) => {
      fetchLoginToken(values, setStatus, setSubmitting)
    },
  })

  /**
   * Initiates the Google Sign-In process.
   * @async
   * @returns {Promise<void>}
   */
  async function SignInByGoogle() {

    setLoadingGoogle(true)
    try {
      const redirectUri = `${(window?.RUNTIME_CONFIG?.REACT_APP_SIGNIN_GOOGLE || process.env.REACT_APP_SIGNIN_GOOGLE)}/auth/sign-in-with-google`
      const data = await authApi.getGoogleOAuthUrl(redirectUri)
      window.location.href = data.authorization_url
      setLoadingGoogle(false)
    } catch (e) {

      setLoadingGoogle(false)
    } finally {
      setLoadingGoogle(false)
    }

  }


  return (
    <div className='card'>
      <div className='px-md-5 py-5 card-body'>
        <div className='d-flex flex-column justify-content-center align-items-center mb-10 p-8 rounded'>
          <h1>{intl.formatMessage({ id: 'Sign In To Panel' })} </h1>
          {challengeNotice === 'expired' && !formik.status ? (
            <div className='font-weight-bold text-danger alert-text' role='alert' data-testid='two-factor-expired'>
              {intl.formatMessage({ id: 'TWO_FACTOR.errors.challengeInvalid' })}
            </div>
          ) : formik.status ? (
            <div className='font-weight-bold text-danger alert-text'>
              {intl.formatMessage({ id: formik.status || '' })}
              { }
            </div>
          ) : (
            <span className='opacity-75'>
              {intl.formatMessage({ id: 'Enter your details to login to your account' })}
            </span>
          )}
        </div>
        <form onSubmit={formik.handleSubmit} noValidate id='kt_login_signin_form'>
          <div data-mdb-input-init className='mb-4 form-outline'>
            <input
              dir='ltr'
              type='email'
              id='form3Example3'
              className={clsx(
                'form-control',
                { 'is-invalid': formik.touched.email && formik.errors.email },
                {
                  'is-valid': formik.touched.email && !formik.errors.email,
                }
              )}
              {...formik.getFieldProps('email')}
              onChange={(e) => { formik.setFieldValue("email", e.target.value.toLowerCase()) }}
            />

            <div className='d-flex justify-content-between align-items-center py-1'>
              <label className='m-0 form-label' htmlFor='form3Example3'>
                {intl.formatMessage({ id: 'Email address' })}
              </label>

              {formik.touched.email && formik.errors.email && (
                <div className='text-center text-danger fv-plugins-message-container'>
                  <span role='alert'>{formik.errors.email}</span>
                </div>
              )}
            </div>
          </div>

          <div data-mdb-input-init className='position-relative mb-4 form-outline'>
            <input
              dir='ltr'
              type={pass ? 'text' : 'password'}
              id='form3Example4'
              className={clsx(
                'form-control',
                { 'is-invalid': formik.touched.password && formik.errors.password },
                {
                  'is-valid': formik.touched.password && !formik.errors.password,
                }
              )}
              placeholder=' '
              {...formik.getFieldProps('password')}
            />
            <span
              className='position-absolute translate-middle btn btn-sm btn-icon me-n2'
              style={{ top: 22, right: 13 }}
              data-kt-password-meter-control='visibility'
              onClick={() => {
                setPass((perv) => !perv)
              }}
            >
              {!pass ? (
                <EyeOff size={24} className='text-white' style={{ cursor: 'pointer' }} />
              ) : (
                <Eye size={24} className='text-white' style={{ cursor: 'pointer' }} />
              )}
            </span>
            <div className='d-flex justify-content-between align-items-center py-1'>
              <label className='m-0 form-label' htmlFor='form3Example4'>
                {intl.formatMessage({ id: 'Password' })}
              </label>
              {formik.touched.password && formik.errors.password && (
                <div className='text-center text-danger fv-plugins-message-container'>
                  <span role='alert'>{formik.errors.password}</span>
                </div>
              )}
            </div>
          </div>

          <div className='d-flex justify-content-center mb-4 form-check'>
            <input
              className='form-check-input me-2'
              type='checkbox'
              value=''
              id='form2Example33'
            // checked
            />
            <label className='form-check-label' htmlFor='form2Example33'>
              {intl.formatMessage({ id: 'Remember me' })}
            </label>
          </div>

          <button
            type='submit'
            id='kt_sign_in_submit'
            className='btn-block w-100 btn btn-lg btn-primary'
            disabled={loginMutation.isPending || loadingGoogle || !formik.isValid}
            style={{ opacity: 0.9 }}
          >
            {!loginMutation.isPending && (
              <span className='indicator-label'>{intl.formatMessage({ id: 'Sign in' })}</span>
            )}
            {loginMutation.isPending && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                {intl.formatMessage({ id: 'Please wait' }) + '...'}

                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>

          <div className='text-center'>
            <div className='d-flex align-items-center my-4 w-100 divider'>
              <hr className='w-50' />
              <p className='mx-3 mb-0 text-center text-muted fw-bold'>
                {intl.formatMessage({ id: 'OR' })}
              </p>
              <hr className='w-50' />
            </div>

            <button
              data-mdb-button-init
              data-mdb-ripple-init
              className='btn-block d-flex justify-content-center align-items-center gap-1 w-100 btn btn-lg btn-primary'
              style={{ backgroundColor: '#dd4b39' }}
              onClick={() => {
                SignInByGoogle()
              }}
              disabled={loginMutation.isPending || loadingGoogle}
            >
              <svg className='me-2' width='16' height='16' viewBox='0 0 48 48' aria-hidden='true'>
                <path fill='#FFC107' d='M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.3 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z' />
                <path fill='#FF3D00' d='M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z' />
                <path fill='#4CAF50' d='M24 44c5.3 0 10.1-2 13.7-5.4l-6.3-5.2C29.4 35 26.8 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z' />
                <path fill='#1976D2' d='M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.6l6.3 5.2C40 35.7 44 30.4 44 24c0-1.3-.1-2.4-.4-3.5z' />
              </svg>
              {!loadingGoogle && (
                <span className='indicator-label'>
                  {intl.formatMessage({ id: 'Sign in with google' })}
                </span>
              )}
              {loadingGoogle && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  {intl.formatMessage({ id: 'Please wait' }) + '...'}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
        <hr className='w-100' />
        <div className='form-group d-flex flex-wrap justify-content-between align-items-center mt-5 px-8'>
          <Link to='/auth/forgot-password' id='kt_login_forgot' className='font-weight-bold'>
            {intl.formatMessage({ id: 'Forget Password ?' })}
          </Link>
          <Link to='/auth/sign-up' id='kt_login_forgot' className='font-weight-bold'>
            {intl.formatMessage({ id: 'Sign up' })}
          </Link>
        </div>
      </div>
    </div>
  )
}
